// google.maps.visualization.HeatmapLayer was removed in Maps JS API 3.65 and its
// constructor now throws, so the density surface is drawn onto our own canvas
// overlay: accumulate a greyscale blob per point, then colourise by alpha.
const HEAT_RADIUS_PX = 26;
const HEAT_MAX_ALPHA = 0.85;
const HEAT_MIN_BLOB_ALPHA = 0.08;
const HEAT_GRADIENT: [number, string][] = [
  [0.0, 'rgba(58, 62, 214, 0)'],
  [0.25, 'rgba(58, 62, 214, 1)'],
  [0.45, 'rgba(0, 197, 219, 1)'],
  [0.65, 'rgba(92, 219, 84, 1)'],
  [0.85, 'rgba(249, 220, 51, 1)'],
  [1.0, 'rgba(228, 48, 30, 1)']
];

export interface ChatHeatPoint {
  position: google.maps.LatLngLiteral;
  weight: number;
}

/**
 * google.maps.OverlayView only exists once the Maps script has run, so the class
 * is declared inside the factory rather than at module scope.
 */
export function createChatHeatmapOverlay(points: ChatHeatPoint[]): google.maps.OverlayView {
  class ChatHeatmapOverlay extends google.maps.OverlayView {
    private canvas?: HTMLCanvasElement;
    private blob?: HTMLCanvasElement;
    private palette?: Uint8ClampedArray;

    override onAdd(): void {
      this.canvas = document.createElement('canvas');
      this.canvas.style.position = 'absolute';
      this.canvas.style.pointerEvents = 'none';
      this.getPanes()?.overlayLayer.appendChild(this.canvas);
    }

    override onRemove(): void {
      this.canvas?.remove();
      this.canvas = undefined;
    }

    override draw(): void {
      const projection = this.getProjection();
      const bounds = (this.getMap() as google.maps.Map | undefined)?.getBounds();

      if (!this.canvas || !projection || !bounds) {
        return;
      }

      const southWest = projection.fromLatLngToDivPixel(bounds.getSouthWest());
      const northEast = projection.fromLatLngToDivPixel(bounds.getNorthEast());

      if (!southWest || !northEast) {
        return;
      }

      const left = Math.min(southWest.x, northEast.x) - HEAT_RADIUS_PX;
      const top = Math.min(southWest.y, northEast.y) - HEAT_RADIUS_PX;
      const width = Math.round(Math.abs(northEast.x - southWest.x)) + HEAT_RADIUS_PX * 2;
      const height = Math.round(Math.abs(southWest.y - northEast.y)) + HEAT_RADIUS_PX * 2;

      if (width <= 0 || height <= 0) {
        return;
      }

      this.canvas.style.left = `${left}px`;
      this.canvas.style.top = `${top}px`;
      this.canvas.width = width;
      this.canvas.height = height;

      const context = this.canvas.getContext('2d');
      if (!context) {
        return;
      }

      const blob = this.getBlob();
      let painted = false;

      points.forEach((point) => {
        const pixel = projection.fromLatLngToDivPixel(
          new google.maps.LatLng(point.position.lat, point.position.lng)
        );

        if (!pixel) {
          return;
        }

        const x = pixel.x - left;
        const y = pixel.y - top;

        if (x < -HEAT_RADIUS_PX || y < -HEAT_RADIUS_PX || x > width + HEAT_RADIUS_PX || y > height + HEAT_RADIUS_PX) {
          return;
        }

        context.globalAlpha = Math.max(point.weight, HEAT_MIN_BLOB_ALPHA);
        context.drawImage(blob, x - HEAT_RADIUS_PX, y - HEAT_RADIUS_PX);
        painted = true;
      });

      if (painted) {
        this.colourise(context, width, height);
      }
    }

    /** One pre-rendered radial falloff, stamped once per point. */
    private getBlob(): HTMLCanvasElement {
      if (this.blob) {
        return this.blob;
      }

      const size = HEAT_RADIUS_PX * 2;
      const blob = document.createElement('canvas');
      blob.width = size;
      blob.height = size;

      const context = blob.getContext('2d')!;
      const gradient = context.createRadialGradient(
        HEAT_RADIUS_PX, HEAT_RADIUS_PX, 0,
        HEAT_RADIUS_PX, HEAT_RADIUS_PX, HEAT_RADIUS_PX
      );

      gradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      context.fillStyle = gradient;
      context.fillRect(0, 0, size, size);

      this.blob = blob;
      return blob;
    }

    /** Accumulated alpha is the density; map it through the colour ramp. */
    private colourise(context: CanvasRenderingContext2D, width: number, height: number): void {
      const palette = this.getPalette();
      const image = context.getImageData(0, 0, width, height);
      const data = image.data;
      const maxAlpha = HEAT_MAX_ALPHA * 255;

      for (let index = 3; index < data.length; index += 4) {
        const alpha = data[index];

        if (!alpha) {
          continue;
        }

        const offset = alpha * 4;
        data[index - 3] = palette[offset];
        data[index - 2] = palette[offset + 1];
        data[index - 1] = palette[offset + 2];
        data[index] = Math.min(alpha, maxAlpha);
      }

      context.putImageData(image, 0, 0);
    }

    /** 256-entry lookup table built from HEAT_GRADIENT. */
    private getPalette(): Uint8ClampedArray {
      if (this.palette) {
        return this.palette;
      }

      const ramp = document.createElement('canvas');
      ramp.width = 1;
      ramp.height = 256;

      const context = ramp.getContext('2d')!;
      const gradient = context.createLinearGradient(0, 0, 0, 256);

      HEAT_GRADIENT.forEach(([stop, colour]) => gradient.addColorStop(stop, colour));
      context.fillStyle = gradient;
      context.fillRect(0, 0, 1, 256);

      this.palette = context.getImageData(0, 0, 1, 256).data;
      return this.palette;
    }
  }

  return new ChatHeatmapOverlay();
}
